/* eslint-disable react/jsx-no-bind, jsx-a11y/no-noninteractive-element-interactions */
import {
  Settings,
  Search,
  Clear,
  Delete,
  RestoreFromTrash,
  Refresh,
  WarningAmber,
} from '@mui/icons-material';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  useTheme,
  Divider,
  Grid,
  InputAdornment,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { S3_URL } from '../../constants/baseUrl';
import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';

import packageTypesService from './packageTypesService';

const PackageTypes = () => {
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: { result: [] },
    statusCode: 0,
  });
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newPackageType, setNewPackageType] = useState({
    packageName: '',
    packageDescription: '',
    shortCode: '',
    order: 0,
    packageIcon: null,
  });
  const [selectedPackageType, setSelectedPackageType] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({
    packageName: '',
    packageIcon: '',
    packageDescription: '',
    shortCode: '',
    order: '',
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [imageErrors, setImageErrors] = useState(new Set());

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Extract and filter package type data
  const packageTypeData = apiResponse.data?.result || [];
  const filteredData = packageTypeData.filter(
    (item) =>
      (search === '' ||
        item.packageName.toLowerCase().includes(search.toLowerCase())) &&
      (showDeleted ? item.isDeleted : !item.isDeleted),
  );

  useEffect(() => {
    fetchPackageTypes();
  }, [showDeleted]);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('package_icons/')) return `${S3_URL}/${url}`;
    return `${S3_URL}/package_icons/${url}`;
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPackageType(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const validateInputs = () => {
    let valid = true;
    const newErrors = {
      packageName: '',
      packageIcon: '',
      packageDescription: '',
      shortCode: '',
      order: '',
    };

    // Create Mode: packageName is required
    if (!isEditMode && !newPackageType.packageName.trim()) {
      newErrors.packageName = 'Package name is required.';
      valid = false;
    }

    // Package name: alphabetic characters and spaces only (allow apostrophe/hyphen for names like "Driver's" or "Same-Day")
    const packageNameTrimmed = newPackageType.packageName.trim();
    if (packageNameTrimmed) {
      const packageNameRegex = /^[A-Za-z\s'-]+$/;
      if (!packageNameRegex.test(packageNameTrimmed)) {
        newErrors.packageName =
          'Package name should contain only letters and spaces.';
        valid = false;
      }
    }

    // Create Mode: packageIcon is required
    if (!isEditMode && !newPackageType.packageIcon) {
      newErrors.packageIcon = 'Package icon required.';
      valid = false;
    }
    // Image size must be below 1MB when a file is selected
    if (newPackageType.packageIcon instanceof File && newPackageType.packageIcon.size > 1024 * 1024) {
      newErrors.packageIcon = 'Image size must be below 1MB.';
      valid = false;
    }
    // Edit Mode: packageIcon is optional

    // Create Mode: packageDescription is required
    if (!isEditMode && !newPackageType.packageDescription.trim()) {
      newErrors.packageDescription = 'Description is required.';
      valid = false;
    }

    // shortCode is optional in both modes; when provided: 2-5 letters only (e.g. TOY, DOC, FRU)
    const shortCodeTrimmed = newPackageType.shortCode.trim();
    if (shortCodeTrimmed) {
      const shortCodeRegex = /^[A-Za-z]{2,5}$/;
      if (!shortCodeRegex.test(shortCodeTrimmed)) {
        if (/^\d+$/.test(shortCodeTrimmed)) {
          newErrors.shortCode =
            'Short code must be letters only (e.g. TOY, DOC, FRU), not numbers.';
        } else if (shortCodeTrimmed.length < 2 || shortCodeTrimmed.length > 5) {
          newErrors.shortCode = 'Short code must be 2–5 characters.';
        } else {
          newErrors.shortCode =
            'Short code must be 2–5 letters only (e.g. TOY, DOC, FRU).';
        }
        valid = false;
      }
    }

    // Create Mode: order_id is required
    if (!isEditMode) {
      if (newPackageType.order === null || newPackageType.order === undefined || newPackageType.order === '') {
        newErrors.order = 'Order is required.';
        valid = false;
      } else if (isNaN(Number(newPackageType.order))) {
        newErrors.order = 'Order must be a valid number.';
        valid = false;
      }
    }
    // Edit Mode: order_id is optional, but if provided, validate it's a number
    else if (newPackageType.order !== null && newPackageType.order !== undefined && newPackageType.order !== '' && isNaN(Number(newPackageType.order))) {
      newErrors.order = 'Order must be a valid number.';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleModalOpen = (editMode = false) => {
    setIsEditMode(editMode);
    if (editMode && selectedPackageType) {
      setNewPackageType({
        packageName: selectedPackageType.packageName,
        packageDescription: selectedPackageType.packagedescription || '',
        shortCode: selectedPackageType.shortcode || '',
        order: selectedPackageType.order || 0,
        packageIcon: selectedPackageType.packageIcon,
      });
    } else {
      setNewPackageType({
        packageName: '',
        packageIcon: null,
        packageDescription: '',
        shortCode: '',
        order: 0,
      });
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewPackageType({
      packageName: '',
      packageIcon: null,
      packageDescription: '',
      shortCode: '',
      order: 0,
    });
    setErrors({
      packageName: '',
      packageIcon: '',
      packageDescription: '',
      shortCode: '',
      order: '',
    });
    setSelectedPackageType(null);
  };

  const fetchPackageTypes = async () => {
    setLoading(true);
    try {
      const res = await packageTypesService.fetchPackageTypes();
      setApiResponse(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Error fetching package types',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchPackageTypes();
    toast.success('Data refreshed successfully');
  };

  const handleAddPackageType = () => {
    if (validateInputs()) {
      setActionLoading(true);

      const formData = new FormData();
      formData.append('packageName', newPackageType.packageName);
      formData.append('packageDescription', newPackageType.packageDescription);
      formData.append('shortCode', newPackageType.shortCode);
      formData.append('order_id', String(newPackageType.order));

      // Append package icon as binary file
      if (newPackageType.packageIcon instanceof File) {
        formData.append('packageIcon', newPackageType.packageIcon);
      }

      packageTypesService
        .addPackageType(formData)
        .then(() => {
          toast.success('Package type created successfully!');
          fetchPackageTypes();
          handleModalClose();
        })
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error adding package type',
          );
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleEditPackageType = () => {
    if (validateInputs()) {
      setActionLoading(true);

      const queryParams = {
        packageTypeId: String(selectedPackageType.packageTypeId),
        packageName: String(newPackageType.packageName || '').trim(),
        packageDescription: String(newPackageType.packageDescription || '').trim(),
        shortCode: String(newPackageType.shortCode || '').trim(),
        order_id: String(newPackageType.order ?? ''),
      };


      // FormData only for the package icon file (if updated)
      let formData = null;
      if (newPackageType.packageIcon instanceof File) {
        formData = new FormData();
        formData.append('packageIcon', newPackageType.packageIcon);
      }

      packageTypesService
        .updatePackageType(selectedPackageType.packageTypeId, queryParams, formData)
        .then(() => {
          toast.success('Package type updated successfully');
          fetchPackageTypes();
          handleModalClose();
        })
        .catch((error) => {
          toast.error(
            error.response?.data?.message || 'Error editing package type',
          );
        })
        .finally(() => setActionLoading(false));
    }
  };

  const handleSoftDelete = () => {
    setActionLoading(true);
    packageTypesService
      .softDeletePackageType(selectedPackageType.packageTypeId)
      .then(() => {
        toast.success(
          `Package type ID ${selectedPackageType.packageTypeId} deleted successfully.`,
        );
        fetchPackageTypes();
        setDeleteDialogOpen(false);
        setSelectedPackageType(null);
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Error soft deleting package type',
        );
      })
      .finally(() => setActionLoading(false));
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    handleSoftDelete();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedPackageType(null);
  };

  const handleDelete = () => {
    setActionLoading(true);
    packageTypesService
      .deletePackageType(selectedPackageType.packageTypeId)
      .then(() => {
        toast.success(
          `Package type ID ${selectedPackageType.packageTypeId} deleted permanently.`,
        );
        fetchPackageTypes();
        handleMenuClose();
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Error deleting package type',
        );
      })
      .finally(() => setActionLoading(false));
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <span className="global_loader"></span>
      </div>
    );
  }

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Package Types</h3>
            <Box display="flex" alignItems="center" gap={2}>
              {/* <FormControlLabel
                control={
                  <Switch
                    checked={showDeleted}
                    onChange={() => setShowDeleted(!showDeleted)}
                    color="primary"
                  />
                }
                label="Show Deleted"
              /> */}
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                  '&:hover': {
                    borderColor: colors.swiftcarry[200],
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={9} md={10}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by package name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, color: 'gray' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ padding: '4px' }}
                        >
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleModalOpen(false)}
                  fullWidth
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                    },
                    height: '40px',
                  }}
                >
                  Add Package Type
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package Type ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package Name
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package Icon
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Short Code
                    </TableCell>
                    {/* <TableCell style={{ fontWeight: 'bold' }}>Order</TableCell> */}
                    {/* <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell> */}
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created On
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created By
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => (
                        <TableRow key={row.packageTypeId}>
                          <TableCell>{row.packageTypeId}</TableCell>
                          <TableCell>{row.packageName}</TableCell>
                          <TableCell>
                            {row.packageIcon &&
                            !imageErrors.has(row.packageTypeId) ? (
                                <img
                                  src={getImageUrl(row.packageIcon)}
                                  alt={row.packageName}
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    objectFit: 'cover',
                                    borderRadius: '5px',
                                  }}
                                  onError={() => {
                                    setImageErrors((prev) =>
                                      new Set(prev).add(row.packageTypeId),
                                    );
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    bgcolor: colors.swiftcarry[100],
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '18px',
                                  }}
                                >
                                📦
                                </Box>
                              )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.shortcode}
                              size="small"
                              sx={{
                                bgcolor: colors.swiftcarry[100],
                                color: 'white',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          {/* <TableCell>{row.order}</TableCell> */}
                          {/* <TableCell>
                            {row.isDeleted ? (
                              <Typography color="error">Deleted</Typography>
                            ) : (
                              <Typography color="success.main">
                                Active
                              </Typography>
                            )}
                          </TableCell> */}
                          <TableCell>{formatToIST(row.createdOn)}</TableCell>
                          <TableCell>{row.createdBy || 'Unknown'}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(event) => handleMenuOpen(event, row)}
                              disabled={actionLoading}
                            >
                              <Settings />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No package types found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiToolbar-root': {
                  display: 'flex',
                  width: '500px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                '& .MuiTablePagination-selectLabel': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-displayedRows': {
                  marginBottom: '0px',
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                },
                '& .MuiTablePagination-input': {
                  minWidth: '30px',
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleModalOpen(true);
                  handleMenuClose();
                }}
              >
                Update
              </DropdownItem>
              {selectedPackageType?.isDeleted ? (
                <DropdownItem disabled>
                  <RestoreFromTrash fontSize="small" sx={{ mr: 1 }} />
                  Restore (Not Available)
                </DropdownItem>
              ) : (
                <DropdownItem onClick={handleDeleteClick}>
                  Delete
                </DropdownItem>
              )}
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningAmber
              sx={{
                fontSize: 28,
                color: 'error.main',
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="span"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <DialogContentText
            id="delete-dialog-description"
            sx={{
              fontSize: '15px',
              lineHeight: 1.6,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            Are you sure you want to delete the package type{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              "{selectedPackageType?.packageName}"
            </Box>
            ?
          </DialogContentText>
          <DialogContentText
            sx={{
              fontSize: '14px',
              color: 'error.main',
              fontWeight: 500,
              mt: 1,
            }}
          >
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              borderColor: 'text.secondary',
              color: 'text.secondary',
              minWidth: 100,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'text.primary',
                color: 'text.primary',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={actionLoading}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              bgcolor: 'error.main',
              minWidth: 100,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                boxShadow: 'none',
              },
            }}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '500px' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            {isEditMode ? 'Update Package Type' : 'Add Package Type'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label={`Package Name ${!isEditMode ? '*' : ''}`}
                variant="outlined"
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.swiftcarry[100],
                  },
                }}
                value={newPackageType.packageName}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z\s'-]/g, '');
                  setNewPackageType({
                    ...newPackageType,
                    packageName: value,
                  });
                  setErrors({ ...errors, packageName: '' });
                }}
                error={!!errors.packageName}
                helperText={errors.packageName}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Short Code"
                variant="outlined"
                fullWidth
                size="small"
                placeholder="e.g. TOY, DOC"
                inputProps={{ maxLength: 5 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.swiftcarry[100],
                  },
                }}
                value={newPackageType.shortCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase();
                  setNewPackageType({
                    ...newPackageType,
                    shortCode: value,
                  });
                  setErrors({ ...errors, shortCode: '' });
                }}
                error={!!errors.shortCode}
                helperText={errors.shortCode}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={`Description ${!isEditMode ? '*' : ''}`}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.swiftcarry[100],
                  },
                }}
                value={newPackageType.packageDescription}
                onChange={(e) => {
                  setNewPackageType({
                    ...newPackageType,
                    packageDescription: e.target.value,
                  });
                  setErrors({ ...errors, packageDescription: '' });
                }}
                error={!!errors.packageDescription}
                helperText={errors.packageDescription}
              />
            </Grid>

            {/* Order (order_id) input hidden — API still uses newPackageType.order
            <Grid item xs={12}>
              <TextField
                label={`Order ${!isEditMode ? '*' : ''}`}
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: colors.swiftcarry[100],
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.swiftcarry[100],
                  },
                }}
                value={newPackageType.order}
                onChange={(e) => {
                  setNewPackageType({
                    ...newPackageType,
                    order: e.target.value === '' ? '' : parseInt(e.target.value) || 0,
                  });
                  setErrors({ ...errors, order: '' });
                }}
                error={!!errors.order}
                helperText={errors.order}
              />
            </Grid>
            */}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Package Icon {!isEditMode && '*'}
            </Typography>

            {newPackageType.packageIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  bgcolor: '#f9f9f9',
                }}
              >
                <img
                  src={
                    typeof newPackageType.packageIcon === 'string'
                      ? getImageUrl(newPackageType.packageIcon)
                      : URL.createObjectURL(newPackageType.packageIcon)
                  }
                  alt="Package Preview"
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '2px solid #ddd',
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Current Icon
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {typeof newPackageType.packageIcon === 'string'
                      ? 'Existing image'
                      : newPackageType.packageIcon.name}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() =>
                    setNewPackageType({
                      ...newPackageType,
                      packageIcon: null,
                    })
                  }
                >
                  Remove
                </Button>
              </Box>
            )}

            <Button
              component="label"
              variant="outlined"
              fullWidth
              size="medium"
              sx={{
                textTransform: 'none',
                borderStyle: newPackageType.packageIcon ? 'dashed' : 'solid',
                borderColor: colors.swiftcarry[100],
                color: colors.swiftcarry[100],
                py: 1.5,
                '&:hover': {
                  borderColor: colors.swiftcarry[200],
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {newPackageType.packageIcon ? '📷 Change Icon' : '📤 Upload Icon'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  if (e.target.files[0]) {
                    setNewPackageType({
                      ...newPackageType,
                      packageIcon: e.target.files[0],
                    });
                    setErrors({ ...errors, packageIcon: '' });
                  }
                }}
              />
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              Image size must be below 1MB.
            </Typography>
            {errors.packageIcon && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {errors.packageIcon}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleModalClose}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={
                isEditMode ? handleEditPackageType : handleAddPackageType
              }
              disabled={actionLoading}
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': { bgcolor: colors.swiftcarry[100] },
              }}
            >
              {actionLoading
                ? 'Processing...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Add Package Type'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PackageTypes;
